// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Video, Zap, CheckCircle2, AlertCircle, Sparkles, Trophy, Send, ArrowLeft, Crown, Medal, DollarSign, Briefcase } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const ContestSubmit = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    review_text: '',
    video_url: ''
  });
  const [wordCount, setWordCount] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user already submitted
  useEffect(() => {
    const checkExistingSubmission = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/contest/1/my-submission`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.id) {
            setExistingSubmission(data);
          }
        }
      } catch (err) {
        console.error('Failed to check submission:', err);
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSubmission();
  }, []);

  // Calculate word count
  useEffect(() => {
    const words = formData.review_text.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [formData.review_text]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (wordCount < 500) {
      setError(`Your review needs at least 500 words to qualify. Current: ${wordCount} words. Keep going!`);
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Please log in to submit your review');
      }

      const response = await fetch(`${API_BASE_URL}/api/contest/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          contest_id: 1,
          ...formData
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Submission failed');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00ffe7]/20 border-t-[#00ffe7] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Loading...</p>
        </div>
      </div>
    );
  }

  // Show "Already Submitted" message if user has existing submission
  if (existingSubmission) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-[#00ffe7] to-emerald-400 mb-6">
              <CheckCircle2 className="h-12 w-12 text-black" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              You've Already Submitted! ✅
            </h2>
            <p className="text-xl text-white/70 mb-4">
              Your review is being evaluated by our team
            </p>
            <p className="text-white/50 text-sm">
              Submitted on {new Date(existingSubmission.submitted_at).toLocaleDateString()}
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8 mb-6">
            <h3 className="text-lg font-bold mb-4">Your Submission</h3>
            <div className="space-y-3">
              <div>
                <p className="text-white/50 text-sm mb-1">Word Count</p>
                <p className="text-[#00ffe7] font-semibold">{existingSubmission.word_count} words</p>
              </div>
              {existingSubmission.video_url && (
                <div>
                  <p className="text-white/50 text-sm mb-1">Video Included</p>
                  <a href={existingSubmission.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 text-sm break-all">
                    {existingSubmission.video_url}
                  </a>
                </div>
              )}
              <div>
                <p className="text-white/50 text-sm mb-1">Status</p>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-sm font-semibold border border-yellow-500/30">
                  {existingSubmission.status === 'pending' ? '⏳ Under Review' : existingSubmission.status.toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={() => navigate('/')}
              className="px-8 py-4 bg-gradient-to-r from-[#00ffe7] to-emerald-400 text-black font-bold rounded-lg hover:scale-105 transition-all duration-300 shadow-lg shadow-[#00ffe7]/30 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Home
            </button>
          </div>

          <div className="mt-8 text-center">
            <p className="text-white/60 text-sm">
              💡 Only one submission per person is allowed. Good luck!
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4 py-12">
        <div className="max-w-2xl w-full">
          {/* Success Animation */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-[#00ffe7] to-emerald-400 mb-6 animate-pulse">
              <Trophy className="h-12 w-12 text-black" />
            </div>
            <h2 className="text-4xl sm:text-5xl font-extrabold mb-4">
              Submission Received! 🎉
            </h2>
            <p className="text-xl text-white/70 mb-8">
              Your review is now being evaluated by our team
            </p>
          </div>

          {/* What's Next */}
          <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8 mb-6">
            <h3 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Sparkles className="h-6 w-6 text-[#00ffe7]" />
              What Happens Next?
            </h3>

            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00ffe7]/20 flex items-center justify-center text-[#00ffe7] font-bold border border-[#00ffe7]/30">
                  1
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Review Evaluation (Nov 29 - Dec 4)</h4>
                  <p className="text-white/70 text-sm">Our team reads every submission and scores based on detail, honesty, and insights.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-bold border border-purple-500/30">
                  2
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Winners Announced (Dec 5)</h4>
                  <p className="text-white/70 text-sm">We'll email the top 3 winners and announce them publicly.</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold border border-emerald-500/30">
                  3
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Prize Payment (Dec 6-10)</h4>
                  <p className="text-white/70 text-sm">Winners receive USDT (TRC20) directly to their wallet address.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/agent-analysis')}
              className="px-6 py-4 border-2 border-white/20 text-white font-bold rounded-lg hover:bg-white/10 hover:border-white/40 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Zap className="h-5 w-5" />
              Keep Exploring
            </button>
            <button
              onClick={() => navigate('/contest')}
              className="px-6 py-4 bg-gradient-to-r from-[#00ffe7] to-emerald-400 text-black font-bold rounded-lg hover:scale-105 transition-all duration-300 shadow-lg shadow-[#00ffe7]/30 flex items-center justify-center gap-2"
            >
              <Trophy className="h-5 w-5" />
              Contest Details
            </button>
          </div>

          {/* Thank You Note */}
          <div className="mt-8 text-center">
            <p className="text-white/60 text-sm">
              🙏 Thank you for helping us build a better product! Your feedback matters.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-white/5 backdrop-blur-lg sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/agent-analysis')}
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back to Testing</span>
            </button>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-white/50">Word Count:</span>
              <span className={`font-bold ${wordCount >= 500 ? 'text-[#00ffe7]' : 'text-yellow-400'}`}>
                {wordCount} / 500
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 text-yellow-400 mb-6">
            <Trophy className="h-4 w-4" />
            <span className="text-sm font-bold">Win Up To $100 + Job Opportunity</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight">
            <span className="bg-gradient-to-r from-[#00ffe7] via-blue-400 to-purple-400 text-transparent bg-clip-text">
              Share Your
            </span>
            <br />
            Honest Feedback
          </h1>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Your insights help us build better tools for crypto traders worldwide
          </p>
        </div>

        {/* Animated Prize Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
          {/* 1st Place - Largest Card */}
          <div className="md:col-span-2 relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/30 to-orange-500/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 animate-pulse-slow"></div>
            <div className="relative bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/40 rounded-2xl p-8 hover:scale-105 transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Crown className="h-10 w-10 text-yellow-400 animate-bounce" style={{ animationDuration: '2s' }} />
                  <div>
                    <div className="text-sm text-yellow-400 font-semibold">1st Place</div>
                    <div className="text-4xl font-extrabold text-white">$100</div>
                  </div>
                </div>
                <Briefcase className="h-6 w-6 text-purple-400 animate-pulse" />
              </div>
              <div className="space-y-2">
                <p className="text-white/90 font-semibold">💰 $100 USDT (TRC20)</p>
                <p className="text-purple-400 font-semibold">🎯 Job Opportunity!</p>
                <p className="text-white/60 text-sm">Join our team as a Product Analyst</p>
              </div>
            </div>
          </div>

          {/* 2nd Place */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-300/20 to-gray-400/20 rounded-2xl blur-lg group-hover:blur-xl transition-all duration-300"></div>
            <div className="relative bg-gradient-to-br from-gray-400/10 to-gray-500/10 border-2 border-gray-400/40 rounded-2xl p-6 hover:scale-105 transition-all duration-300 h-full">
              <div className="flex items-center gap-2 mb-3">
                <Medal className="h-8 w-8 text-gray-300 animate-pulse" style={{ animationDuration: '2.5s' }} />
                <div>
                  <div className="text-xs text-gray-300 font-semibold">2nd Place</div>
                  <div className="text-3xl font-extrabold text-white">$50</div>
                </div>
              </div>
              <p className="text-white/90 font-semibold">💵 $50 USDT</p>
              <p className="text-white/60 text-sm mt-1">Direct to wallet</p>
            </div>
          </div>

          {/* 3rd Place */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-700/20 to-orange-800/20 rounded-2xl blur-lg group-hover:blur-xl transition-all duration-300"></div>
            <div className="relative bg-gradient-to-br from-orange-700/10 to-orange-800/10 border-2 border-orange-700/40 rounded-2xl p-6 hover:scale-105 transition-all duration-300 h-full">
              <div className="flex items-center gap-2 mb-3">
                <Medal className="h-8 w-8 text-orange-400 animate-pulse" style={{ animationDuration: '3s' }} />
                <div>
                  <div className="text-xs text-orange-400 font-semibold">3rd Place</div>
                  <div className="text-3xl font-extrabold text-white">$25</div>
                </div>
              </div>
              <p className="text-white/90 font-semibold">💵 $25 USDT</p>
              <p className="text-white/60 text-sm mt-1">Direct to wallet</p>
            </div>
          </div>
        </div>

        {/* Info Banner - Testing Reminder */}
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <Sparkles className="h-6 w-6 text-[#00ffe7]" />
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2">Haven't tested the platform yet?</h3>
              <p className="text-white/70 mb-4">
                You need to actually use InvestVerse.AI before writing your review. Try analyzing coins with our AI agents, explore different strategies, and test various features.
              </p>
              <button
                onClick={() => navigate('/agent-analysis')}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg font-medium transition-all text-sm"
              >
                <Zap className="h-4 w-4" />
                Start Testing Now
              </button>
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8 sm:p-10 mb-8">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Review Text Area */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 text-lg font-bold">
                  <FileText className="h-5 w-5 text-[#00ffe7]" />
                  Your Review *
                </label>
                <span className={`text-sm font-semibold px-3 py-1 rounded-full ${
                  wordCount >= 500
                    ? 'bg-[#00ffe7]/20 text-[#00ffe7] border border-[#00ffe7]/30'
                    : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                }`}>
                  {wordCount >= 500 ? '✓ Minimum met!' : `${500 - wordCount} more words needed`}
                </span>
              </div>

              <textarea
                name="review_text"
                value={formData.review_text}
                onChange={handleInputChange}
                rows={20}
                className="w-full px-4 py-4 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white resize-none placeholder-white/40 transition-colors"
                placeholder="Write your honest, detailed review here...

What should you cover? Here are some prompts to get you started:

🧪 Testing Experience:
• Which features did you test? (AI Agents, Market Analysis, Strategy Explorer, etc.)
• How long did you spend exploring the platform?
• Was it easy to understand and navigate?

✅ What Worked Well:
• Which features impressed you the most?
• What problems does InvestVerse.AI solve for crypto traders?
• Would you actually use this for real trading? Why or why not?

❌ What Needs Improvement:
• What confused you or didn't work as expected?
• Any bugs or technical issues you encountered?
• What features are missing that you'd want to see?

💡 Suggestions:
• If you could change 3 things, what would they be?
• Who would benefit most from using InvestVerse.AI?
• What would make this a must-have tool?

Remember: Be specific, honest, and detailed. Generic reviews like 'It's great!' won't win prizes. Tell your story!"
                required
              />

              <div className="mt-2 text-sm text-white/50">
                💡 <strong>Pro Tip:</strong> Use specific examples from your testing. "The Market Analysis helped me identify BTC's RSI was oversold" is way better than "The charts are cool."
              </div>
            </div>

            {/* Video URL (Optional) */}
            <div>
              <label className="flex items-center gap-2 text-lg font-bold mb-3">
                <Video className="h-5 w-5 text-purple-400" />
                Video Walkthrough (Optional - Bonus Points!)
              </label>

              <input
                type="url"
                name="video_url"
                value={formData.video_url}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:border-[#00ffe7] text-white placeholder-white/40 transition-colors"
                placeholder="https://youtube.com/watch?v=... or https://loom.com/..."
              />

              <div className="mt-2 bg-purple-500/10 border border-purple-500/20 rounded-lg p-4">
                <p className="text-sm text-white/70 mb-2">
                  <strong className="text-purple-400">🎥 Want to stand out?</strong> Record a quick 3-5 minute video walkthrough:
                </p>
                <ul className="text-sm text-white/60 space-y-1 ml-4">
                  <li>• Show yourself using InvestVerse.AI features</li>
                  <li>• Share your screen and talk through your experience</li>
                  <li>• Use Loom, YouTube, or Google Drive (make sure it's viewable)</li>
                  <li>• Not required, but significantly boosts your chances of winning!</li>
                </ul>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || wordCount < 500}
              className="w-full px-8 py-5 bg-gradient-to-r from-[#00ffe7] to-emerald-400 text-black font-bold text-lg rounded-lg hover:scale-[1.02] transition-all duration-300 shadow-lg shadow-[#00ffe7]/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-black/20 border-t-black rounded-full animate-spin"></div>
                  Submitting Your Review...
                </>
              ) : (
                <>
                  <Send className="h-5 w-5" />
                  Submit Review & Enter Contest
                </>
              )}
            </button>

            {wordCount < 500 && (
              <p className="text-center text-yellow-400 text-sm">
                ⚠️ You need at least {500 - wordCount} more words to submit
              </p>
            )}
          </form>
        </div>

        {/* Guidelines */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#00ffe7]/20 flex items-center justify-center border border-[#00ffe7]/30">
                <CheckCircle2 className="h-5 w-5 text-[#00ffe7]" />
              </div>
              <h3 className="font-bold">Be Honest</h3>
            </div>
            <p className="text-white/70 text-sm">
              We want brutal honesty. Tell us what sucks and what's amazing. Sugar-coating helps nobody.
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center border border-purple-500/30">
                <CheckCircle2 className="h-5 w-5 text-purple-400" />
              </div>
              <h3 className="font-bold">Be Specific</h3>
            </div>
            <p className="text-white/70 text-sm">
              Don't just say "it's good" - explain what you tested, what happened, and how it felt to use.
            </p>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center border border-emerald-500/30">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              </div>
              <h3 className="font-bold">Be Helpful</h3>
            </div>
            <p className="text-white/70 text-sm">
              Give us actionable suggestions. Your feedback directly shapes our roadmap.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContestSubmit;
